import { createFileRoute, Link } from '@tanstack/react-router';
import { useStore } from '@tanstack/react-store';
import { Loader2Icon } from 'lucide-react';
import * as React from 'react';
import { DayButton } from '~/components/programme-list';
import artists from '~/data/artists.json';
import { cn } from '~/lib/utils';
import { PageSection } from '~/routes/__root';
import { formatDate, formatTime, langStore } from '~/store/langStore';

export const Route = createFileRoute('/program/')({
  component: RouteComponent,
});

interface Artist {
  'Name': string;
  'Slug': string;
  'Image': string;
  'Country'?: string;
  'Stage': string;
  'Day': string;
  'Facebook'?: string;
  'Instagram'?: string;
  'Music'?: string;
  'Desc-HU'?: string;
  'Desc-EN'?: string;
  'Desc-RS'?: string;
  'Ready'?: boolean;
}

interface DaySchedule {
  date: string;
  dayNumber: number;
  stages: Record<string, Artist[]>;
}

function splitAndSortArtists(artists: Artist[]): Artist[] {
  function getHour(artist: Artist) {
    const date = new Date(artist.Day);

    return date.getHours() + date.getMinutes() / 60;
  }

  const firstHalf = artists
    .filter((a) => {
      return getHour(a) < 8 || getHour(a) >= 23;
    })
    .sort((a, b) => new Date(a.Day).getTime() - new Date(b.Day).getTime());

  const secondHalf = artists
    .filter((a) => {
      return getHour(a) >= 8 && getHour(a) < 23;
    })
    .sort((a, b) => new Date(a.Day).getTime() - new Date(b.Day).getTime());

  return [...secondHalf, ...firstHalf];
}

const STAGE_ORDER = [
  'Bitang stage',
  'Akácos stage',
  'Etnocamp',
  'BaZen stage',
  'NoChill stage',
];

function RouteComponent() {
  const locale = useStore(langStore, s => s);
  const [selectedDay, setSelectedDay] = React.useState<string>('');
  const [scheduleData, setScheduleData] = React.useState<DaySchedule[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const dayMap = new Map<string, DaySchedule>();

    artists.forEach((artist) => {
      if (!artist.Ready || !artist.Day || !artist.Stage)
        return;

      const date = new Date(artist.Day);

      const dateKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
      const dayNumber = date.getDate();

      if (!dayMap.has(dateKey)) {
        dayMap.set(dateKey, {
          date: dateKey,
          dayNumber,
          stages: {},
        });
      }

      const daySchedule = dayMap.get(dateKey)!;
      if (!daySchedule.stages[artist.Stage]) {
        daySchedule.stages[artist.Stage] = [];
      }

      daySchedule.stages[artist.Stage].push(artist);
    });

    dayMap.forEach((daySchedule) => {
      Object.keys(daySchedule.stages).forEach((stage) => {
        daySchedule.stages[stage].sort((a, b) =>
          new Date(a.Day).getTime() - new Date(b.Day).getTime(),
        );
      });
    });

    const sortedDays = Array.from(dayMap.values()).sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    if (sortedDays.length > 0 && !selectedDay) {
      setSelectedDay(sortedDays[0].date);
    }

    setScheduleData(sortedDays);
    setLoading(false);
  }, [selectedDay]);

  if (loading) {
    return (
      <PageSection>
        <div className="flex justify-center py-12">
          <Loader2Icon className="h-10 w-10 animate-spin" />
        </div>
      </PageSection>
    );
  }

  if (scheduleData.length === 0) {
    return (
      <PageSection>
        <div className="py-12 text-center">
          <h1 className="mb-4 text-2xl font-bold">
            {locale === 'HU' && 'Nincs még elérhető program'}
            {locale === 'EN' && 'No schedule available yet'}
            {locale === 'RS' && 'Raspored još nije dostupan'}
          </h1>
        </div>
      </PageSection>
    );
  }

  return (
    <PageSection width="narrow" padding="small">
      {/* Day tabs */}
      <div className="mb-4 flex flex-wrap justify-center gap-2 md:mb-8">
        {scheduleData.map(day => (
          <DayButton
            key={day.date}
            date={day.date}
            isSelected={selectedDay === day.date}
            onClick={() => setSelectedDay(day.date)}
          />
        ))}
      </div>

      {/* Schedule for selected day */}
      {scheduleData.map(day => (
        <div
          key={day.date}
          className={cn(
            'space-y-4 md:space-y-8',
            selectedDay === day.date ? 'block' : 'hidden',
          )}
        >
          <div className="mb-3 text-center md:mb-6">
            <h1 className="text-lg font-bold text-black md:text-2xl">
              {formatDate(day.date)}
            </h1>
          </div>

          {/* Stages */}
          {Object.entries(day.stages)
            .sort(([a], [b]) => {
              const indexA = STAGE_ORDER.indexOf(a);
              const indexB = STAGE_ORDER.indexOf(b);
              if (indexA === -1 && indexB === -1) {
                return a.localeCompare(b);
              }
              if (indexA === -1)
                return 1;
              if (indexB === -1)
                return -1;
              return indexA - indexB;
            })
            .map(([stageName, stageArtists]) => (
              <div key={stageName} className="">
                <h2 className="border-red/20 text-red border-b pb-2 text-lg font-semibold md:pb-4 md:text-xl">
                  {stageName}
                </h2>

                <div className="grid gap-2 md:gap-4">
                  {splitAndSortArtists(stageArtists).map((artist, index) => (
                    <div
                      key={`${artist.Slug}-${index}`}
                      className="rounded-lg bg-white/5 p-2 transition-colors duration-200 hover:bg-white/10 sm:p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            {/* <div className="flex-shrink-0">
                            <img
                              src={artist.Image}
                              alt={artist.Name}
                              className="h-12 w-12 rounded-full object-cover"
                              loading="lazy"
                            />
                          </div> */}
                            <div className="min-w-0 flex-1">
                              <Link to="/artists/$slug" params={{ slug: artist.Slug }} className="">
                                <h3 className="truncate font-semibold text-black">
                                  {artist.Name}
                                </h3>
                                <p className="text-sm text-gray-300">
                                  {artist.Country}
                                </p>
                              </Link>
                            </div>
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          <div className="text-right">
                            <div className="text-red text-lg font-bold">
                              {formatTime(artist.Day)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      ))}
    </PageSection>
  );
}

export default RouteComponent;
